
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Asset, AssetCategory, Institution, Sector } from '../types';
import { 
  X, 
  Plus, 
  ChevronDown, 
  Search, 
  Check, 
  ChevronRight, 
  ShieldCheck, 
  Wallet,
  TrendingUp,
  Landmark,
  Building2,
  FileText,
  MapPin,
  Briefcase,
  Shield,
  Save,
  Lock,
  Stamp,
  Globe,
  Coins,
  Cpu,
  Smartphone,
  Home,
  Loader2,
  Calendar,
  Layers,
  Activity,
  AlertCircle,
  QrCode,
  Info
} from 'lucide-react';
import { dbService } from '../services/dbService';

interface AddAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (asset: Asset) => void;
  initialData?: Asset | null;
}

const FALLBACK_SECTORS: Sector[] = [
  {
    id: 's1', name: 'Banking & Deposits', categories: [
      { id: 'c1', sector_id: 's1', name: 'Savings Account', display_name: 'Savings Account' },
      { id: 'c2', sector_id: 's1', name: 'Current Account', display_name: 'Current Account' },
      { id: 'c3', sector_id: 's1', name: 'Fixed Deposit (FD)', display_name: 'Fixed Deposit (FD)' },
      { id: 'c4', sector_id: 's1', name: 'Recurring Deposit (RD)', display_name: 'Recurring Deposit (RD)' },
      { id: 'c5', sector_id: 's1', name: 'Safe Deposit Locker', display_name: 'Safe Deposit Locker' },
    ]
  },
  {
    id: 's2', name: 'Investments', categories: [
      { id: 'c6', sector_id: 's2', name: 'Mutual Funds', display_name: 'Mutual Funds' },
      { id: 'c7', sector_id: 's2', name: 'Demat Account', display_name: 'Demat Account' },
    ]
  }
];

const BankLogo: React.FC<{ svg: string; name: string; className?: string }> = ({ svg, name, className }) => {
  const sanitizedSvg = useMemo(() => {
    if (!svg) return null;
    let processed = svg.trim();
    if (!processed.includes('viewBox')) {
        processed = processed.replace('<svg', '<svg viewBox="0 0 100 100"');
    }
    return processed.replace(/width=["'][^"']*["']/g, '').replace(/height=["'][^"']*["']/g, '');
  }, [svg]);

  if (sanitizedSvg) {
    return <div className={`${className} flex items-center justify-center [&_svg]:w-full [&_svg]:h-full`} dangerouslySetInnerHTML={{ __html: sanitizedSvg }} />;
  }
  return <div className={`${className} bg-slate-100 flex items-center justify-center text-slate-400 font-black text-[10px] rounded-lg`}>{name.charAt(0)}</div>;
};

export const AddAssetModal: React.FC<AddAssetModalProps> = ({ isOpen, onClose, onAdd, initialData }) => {
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [isLoadingTaxonomy, setIsLoadingTaxonomy] = useState(true);
  const [selectedSectorId, setSelectedSectorId] = useState<string>('');
  
  const [instSearch, setInstSearch] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState<Partial<Asset>>({
    name: '',
    category: '',
    institution: '',
    value: 0,
    nominationStatus: true,
    lastUpdated: new Date().toISOString().split('T')[0],
    accountType: '',
    modeOfOperation: 'Single',
    customerId: '',
    ifscCode: '',
    branchName: '',
    branchDetails: ''
  });

  useEffect(() => {
    let isMounted = true;
    if (isOpen) {
      setIsLoadingTaxonomy(true);
      
      const safetyTimer = setTimeout(() => {
        if (isMounted && isLoadingTaxonomy) {
          setIsLoadingTaxonomy(false);
          if (sectors.length === 0) setSectors(FALLBACK_SECTORS);
        }
      }, 5000);

      const loadData = async () => {
        try {
          const [fetchedSectors, fetchedInstitutions] = await Promise.all([
            dbService.getTaxonomy(),
            dbService.getInstitutions()
          ]);
          
          if (!isMounted) return;

          const activeSectors = fetchedSectors.length > 0 ? fetchedSectors : FALLBACK_SECTORS;
          setSectors(activeSectors);
          setInstitutions(fetchedInstitutions);
          
          if (initialData) {
            setFormData({ ...initialData });
            setInstSearch(initialData.institution || '');
            const foundSector = activeSectors.find(s => s.categories.some(c => c.name === initialData.category));
            if (foundSector) setSelectedSectorId(foundSector.id);
          } else {
            setSelectedSectorId(activeSectors[0].id);
            setFormData(prev => ({ 
              ...prev, 
              category: activeSectors[0].categories[0]?.name || '',
              accountType: activeSectors[0].categories[0]?.display_name || ''
            }));
          }
        } catch (err) {
          console.error("Modal data load failed:", err);
          if (isMounted) setSectors(FALLBACK_SECTORS);
        } finally {
          if (isMounted) setIsLoadingTaxonomy(false);
          clearTimeout(safetyTimer);
        }
      };

      loadData();
    }
    return () => { isMounted = false; };
  }, [isOpen, initialData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const currentSector = useMemo(() => 
    sectors.find(s => s.id === selectedSectorId) || null, 
  [selectedSectorId, sectors]);

  const isBanking = useMemo(() => 
    currentSector?.name.toLowerCase().includes('banking') || currentSector?.name.toLowerCase().includes('deposit'),
  [currentSector]);

  const filteredInstitutions = useMemo(() => {
    if (!instSearch) return institutions.slice(0, 10);
    return institutions.filter(inst => 
      inst.name.toLowerCase().includes(instSearch.toLowerCase())
    ).slice(0, 8);
  }, [instSearch, institutions]);

  const handleSectorChange = (sectorId: string) => {
    const newSector = sectors.find(s => s.id === sectorId);
    if (newSector) {
      setSelectedSectorId(sectorId);
      setFormData(prev => ({ 
        ...prev, 
        category: newSector.categories[0]?.name || '',
        accountType: newSector.categories[0]?.display_name || ''
      }));
    }
  };

  const isFormValid = !!(formData.name && formData.value !== undefined && formData.value >= 0 && (formData.institution || instSearch));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid) {
      onAdd({
        ...formData as Asset,
        institution: formData.institution || instSearch,
        id: formData.id || `a-${Date.now()}`,
        allocations: formData.allocations || []
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/80 backdrop-blur-md p-4 animate-fade-in">
      <div className="bg-white w-full max-w-2xl rounded-[3rem] shadow-2xl overflow-hidden border border-slate-200 flex flex-col max-h-[92vh]">
        
        <div className="px-10 py-8 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="bg-indigo-600 p-3 rounded-2xl text-white shadow-xl shadow-indigo-100"><Layers size={24} /></div>
            <div>
              <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                {initialData ? 'Refine Asset Details' : 'Secure New Heritage'}
              </h2>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <ShieldCheck size={12} className="text-emerald-500" /> Vault Master Protocol
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-300 hover:text-rose-500 transition-colors"><X size={24} /></button>
        </div>

        {isLoadingTaxonomy ? (
          <div className="p-20 flex flex-col items-center justify-center gap-4">
            <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Encrypting Taxonomy Tunnel...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto custom-scrollbar p-10 space-y-10">
            
            <section className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-1.5 h-6 bg-indigo-600 rounded-full"></div>
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">Asset Identity</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nickname *</label>
                  <input required type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold transition-all shadow-sm" placeholder="e.g. My Savings Fund" />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Regulatory Sector</label>
                  <div className="relative">
                    <select value={selectedSectorId} onChange={(e) => handleSectorChange(e.target.value)} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold transition-all appearance-none cursor-pointer">
                      {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <ChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-indigo-50/50 p-8 rounded-[2.5rem] border border-indigo-100 space-y-8">
              <div className="flex items-center gap-3">
                <Info size={16} className="text-indigo-600" />
                <h3 className="text-[10px] font-black text-slate-600 uppercase tracking-widest">Nomination Metadata</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Account / Policy Type *</label>
                  <select required value={formData.accountType} onChange={(e) => setFormData({...formData, accountType: e.target.value})} className="w-full px-5 py-4 bg-white border border-indigo-100 rounded-2xl outline-none text-sm font-bold shadow-sm">
                    {currentSector?.categories.map(c => <option key={c.id} value={c.display_name}>{c.display_name}</option>)}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Sync Date (Last Updated)</label>
                  <div className="relative">
                    <Calendar className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                    <input type="date" value={formData.lastUpdated || ''} onChange={(e) => setFormData({...formData, lastUpdated: e.target.value})} className="w-full pl-16 pr-6 py-4 bg-white border border-indigo-100 rounded-2xl outline-none text-sm font-bold shadow-sm" />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-6 bg-white border border-indigo-100 rounded-3xl shadow-sm group hover:border-indigo-300 transition-all">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${formData.nominationStatus ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                    <ShieldCheck size={24} />
                  </div>
                  <div>
                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Nomination Registered?</div>
                    <div className="text-xs font-bold text-slate-700">Formal nomination filed with provider</div>
                  </div>
                </div>
                <button type="button" onClick={() => setFormData({...formData, nominationStatus: !formData.nominationStatus})} className={`px-8 py-3 rounded-2xl text-[10px] font-black transition-all shadow-md ${formData.nominationStatus ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {formData.nominationStatus ? 'YES' : 'NO'}
                </button>
              </div>
            </section>

            <section className="space-y-8">
              <div className="flex items-center gap-3">
                <div className="w-1.5 h-6 bg-indigo-600 rounded-full"></div>
                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest">Provider Intelligence</h3>
              </div>

              <div className="space-y-6">
                {/* Searchable Institution Dropdown powered by bank_institutions */}
                <div className="space-y-2 relative" ref={dropdownRef}>
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                    {isBanking ? 'Bank Name' : 'Financial Institution'} *
                  </label>
                  <div className="relative">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                    <input 
                      required 
                      type="text" 
                      value={instSearch} 
                      onFocus={() => setIsDropdownOpen(true)}
                      onChange={(e) => {
                        setInstSearch(e.target.value);
                        setFormData({...formData, institution: e.target.value});
                        setIsDropdownOpen(true);
                      }} 
                      className="w-full pl-16 pr-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold transition-all shadow-sm" 
                      placeholder={`Search ${isBanking ? 'verified banks' : 'institutions'}...`} 
                    />
                  </div>
                  
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 z-50 mt-2 bg-white border border-slate-100 rounded-3xl shadow-2xl max-h-60 overflow-y-auto custom-scrollbar animate-fade-in p-2">
                       {filteredInstitutions.length > 0 ? filteredInstitutions.map((inst) => (
                         <button 
                            key={inst.id} 
                            type="button" 
                            onClick={() => {
                              setInstSearch(inst.name);
                              setFormData({...formData, institution: inst.name});
                              setIsDropdownOpen(false);
                            }}
                            className="w-full flex items-center gap-4 p-3 hover:bg-slate-50 rounded-2xl transition-all text-left"
                          >
                            <div className="w-10 h-10 rounded-xl bg-white border border-slate-100 p-1">
                               <BankLogo svg={inst.logoSvg} name={inst.name} className="w-full h-full" />
                            </div>
                            <div>
                               <div className="text-sm font-black text-slate-900">{inst.name}</div>
                               <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Verified Provider</div>
                            </div>
                         </button>
                       )) : (
                        <div className="p-6 text-center">
                          <p className="text-xs font-bold text-slate-400 uppercase">No verified banks found</p>
                          <p className="text-[10px] text-slate-300 mt-1 italic">Type manually to add your provider</p>
                        </div>
                       )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{isBanking ? 'Account Number' : 'Folio / Policy Number'}</label>
                    <div className="relative">
                      <QrCode className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                      <input type="text" value={formData.customerId} onChange={(e) => setFormData({...formData, customerId: e.target.value})} className="w-full pl-16 pr-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold" placeholder="Primary Identifier" />
                    </div>
                  </div>

                  {isBanking ? (
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">IFSC Code</label>
                      <input type="text" value={formData.ifscCode || ''} onChange={(e) => setFormData({...formData, ifscCode: e.target.value.toUpperCase()})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold uppercase" placeholder="HDFC0001234" />
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Branch / Location</label>
                      <input type="text" value={formData.branchDetails || ''} onChange={(e) => setFormData({...formData, branchDetails: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold" placeholder="Main Branch" />
                    </div>
                  )}
                </div>

                {isBanking && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Branch Name</label>
                      <input type="text" value={formData.branchName || ''} onChange={(e) => setFormData({...formData, branchName: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold" placeholder="MG Road Branch" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Holding Mode</label>
                      <select value={formData.modeOfOperation} onChange={(e) => setFormData({...formData, modeOfOperation: e.target.value as any})} className="w-full px-6 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-indigo-600 outline-none text-sm font-bold transition-all cursor-pointer">
                        <option value="Single">Self / Single</option>
                        <option value="Either or Survivor">Either or Survivor</option>
                        <option value="Joint">Jointly</option>
                        <option value="Former or Survivor">Former or Survivor</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="bg-slate-900 p-10 rounded-[3rem] relative overflow-hidden group/val shadow-2xl">
                  <div className="absolute top-0 right-0 p-8 text-white/5 group-hover/val:scale-110 transition-transform"><Activity size={120} /></div>
                  <div className="relative z-10 space-y-4">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Estimated Asset Value (₹) *</label>
                    <div className="relative max-w-sm">
                        <span className="absolute left-8 top-1/2 -translate-y-1/2 text-slate-500 text-3xl font-black group-focus-within/val:text-indigo-500">₹</span>
                        <input type="number" required value={formData.value || ''} onChange={(e) => setFormData({...formData, value: Number(e.target.value)})} className="w-full pl-16 pr-8 py-6 bg-white/5 border-2 border-transparent rounded-3xl focus:border-indigo-600 outline-none text-3xl font-black text-white shadow-inner" placeholder="0" />
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="pt-8 border-t border-slate-100 flex flex-col sm:flex-row justify-end items-center gap-6">
              <button type="button" onClick={onClose} className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-slate-900 transition-colors">Discard Draft</button>
              <button type="submit" disabled={!isFormValid} className={`group px-12 py-5 rounded-3xl font-black text-[11px] uppercase tracking-[0.3em] transition-all duration-500 flex items-center justify-center gap-3 shadow-2xl active:scale-95 ${isFormValid ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-slate-100 text-slate-300'}`}>
                {initialData ? <Save size={20} /> : <Shield size={20} />}
                <span>{initialData ? 'Update Vault' : 'Secure Asset'}</span>
                <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
